import { useState, useEffect } from "react";
import { X, ArrowRight, Loader2, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * 3-step self-serve checkout drawer.
 * Steps: 1) Brand info  2) Goal brief  3) Confirmation → Stripe redirect
 */
export default function StartInstantlyDrawer({ open, onClose, slug, svc }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [brand, setBrand] = useState("");
  const [website, setWebsite] = useState("");
  const [goal, setGoal] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setBusy(false);
    }
  }, [open, slug]);

  if (!open || !svc) return null;

  const canNext1 = brand.trim().length >= 2;
  const canNext2 = goal.trim().length >= 5;

  const confirm = async () => {
    setBusy(true);
    try {
      // Record intake alongside the checkout for context
      try {
        await api.post("/projects/initialize", {
          tracks: [svc.category || "consult"],
          timeline: "immediate",
          name: brand.trim(),
          email: user?.email || "portal-self-serve@traceelements.io",
          company: brand.trim(),
          brief: `[${svc.name}] Goal: ${goal.trim()}\n\nWebsite: ${website.trim() || "—"}\n\nNotes: ${notes.trim() || "—"}\n\nInitiated by portal user: ${user?.email || "anonymous"}`,
        });
      } catch {
        /* non-blocking */
      }
      const { data } = await api.post("/payments/checkout", {
        package_id: slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) || "Checkout failed";
      toast.error(`ERROR · ${msg}`);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="start-instantly-drawer"
    >
      {/* backdrop */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
      />

      {/* drawer */}
      <div className="relative w-full max-w-lg h-full bg-[#0d1117] border-l border-[#1F2833] overflow-y-auto animate-in slide-in-from-right duration-200">
        <div className="sticky top-0 z-10 bg-[#0d1117] border-b border-[#1F2833] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
              // SELF-SERVE.CHECKOUT
            </div>
            <div className="mt-1 font-display text-lg font-bold tracking-tight text-[#F5F5F7]">
              {svc.name}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="p-2 text-[#C5C6C7] hover:text-[#FF3355] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-4 flex items-center gap-2">
          {[1, 2, 3].map((n) => {
            const active = step === n;
            const done = step > n;
            return (
              <div key={n} className="flex items-center gap-2 flex-1">
                <div
                  className={`h-7 w-7 flex items-center justify-center font-mono text-[10px] font-bold border transition-all ${
                    done
                      ? "border-emerald-400 bg-emerald-400 text-[#0B0C10]"
                      : active
                      ? "border-[#FF3355] text-[#FF3355] bg-[#FF3355]/10"
                      : "border-[#1F2833] text-[#C5C6C7]"
                  }`}
                >
                  {done ? <Check size={12} /> : n}
                </div>
                {n < 3 && (
                  <div
                    className={`flex-1 h-px transition-colors ${
                      step > n ? "bg-emerald-400" : "bg-[#1F2833]"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="px-6 pb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
          {step === 1 && "> Step 01 · Brand info"}
          {step === 2 && "> Step 02 · Goal brief"}
          {step === 3 && "> Step 03 · Confirmation"}
        </div>

        <div className="px-6 pb-6 space-y-4">
          {step === 1 && (
            <>
              <FormField label="brand / company name *">
                <input
                  data-testid="drawer-brand"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="e.g. North Atlas Capital"
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
                />
              </FormField>
              <FormField label="website (optional)">
                <input
                  data-testid="drawer-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://…"
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
                />
              </FormField>
            </>
          )}

          {step === 2 && (
            <>
              <FormField label="what outcome do you need? *">
                <textarea
                  data-testid="drawer-goal"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  rows={4}
                  placeholder="e.g. Drive 40 SQLs / mo from local search in 90 days"
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none p-4 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40 resize-y"
                />
              </FormField>
              <FormField label="anything else we should know?">
                <textarea
                  data-testid="drawer-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Existing tools, integrations, deadlines…"
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none p-4 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40 resize-y"
                />
              </FormField>
            </>
          )}

          {step === 3 && (
            <div className="border border-[#1F2833] bg-[#0B0C10] p-5" data-testid="drawer-confirm">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
                &gt; review &amp; pay
              </div>
              <div className="space-y-3 font-mono text-xs">
                <Row k="Package" v={svc.name} />
                <Row k="Price" v={svc.priceLabel} accent />
                <Row k="Brand" v={brand} />
                {website && <Row k="Website" v={website} />}
                <Row k="Goal" v={goal} multiline />
                {notes && <Row k="Notes" v={notes} multiline />}
              </div>
              <div className="mt-5 border-t border-[#1F2833] pt-4 font-mono text-[10px] text-[#C5C6C7]/70 leading-relaxed">
                &gt; Clicking &quot;Pay with Stripe&quot; opens a secure Stripe checkout in this
                window. Your intake will be attached to the resulting engagement.
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#0d1117] border-t border-[#1F2833] px-6 py-4 flex items-center justify-between gap-3">
          {step > 1 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              data-testid="drawer-back"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C5C6C7] hover:text-[#F5F5F7] transition-colors"
            >
              ← Back
            </button>
          ) : (
            <span />
          )}

          {step === 1 && (
            <button
              onClick={() => setStep(2)}
              disabled={!canNext1}
              data-testid="drawer-next-1"
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ArrowRight size={13} />
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              disabled={!canNext2}
              data-testid="drawer-next-2"
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Review <ArrowRight size={13} />
            </button>
          )}
          {step === 3 && (
            <button
              onClick={confirm}
              disabled={busy}
              data-testid="drawer-pay"
              className="inline-flex items-center gap-2 bg-emerald-400 text-[#0B0C10] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Redirecting
                </>
              ) : (
                <>
                  <CreditCard size={13} /> Pay with Stripe
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2">
        &gt; {label}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v, accent, multiline }) {
  return (
    <div className={`grid ${multiline ? "grid-cols-1" : "grid-cols-[110px_1fr]"} gap-2`}>
      <div className="text-[#C5C6C7]/70 uppercase tracking-[0.18em] text-[10px]">{k}</div>
      <div className={`text-sm ${accent ? "text-[#FF3355] font-bold" : "text-[#F5F5F7]"}`}>
        {v}
      </div>
    </div>
  );
}
